/**
 * @bolao/shared — contracts used by both the API and the web app.
 *
 * Zod schemas live here rather than in either app, so one definition serves
 * request validation, TypeScript types, and client-side form validation.
 * Types alone are not enough: they vanish at runtime, and prediction input
 * feeds a scoring engine where bad data corrupts standings silently.
 */
import { z } from 'zod';

/** ISO 639-1 style locale tags the UI supports at launch. */
export const SUPPORTED_LOCALES = ['en', 'pt-BR'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: Locale = 'en';

export const localeSchema = z.enum(SUPPORTED_LOCALES);

/**
 * A predicted or real scoreline.
 *
 * Goals are capped at a deliberately generous but finite bound: unbounded
 * integers let a client poison the proximity softmax with absurd distances.
 */
export const MAX_GOALS = 99;

export const scoreSchema = z.object({
  home: z.number().int().min(0).max(MAX_GOALS),
  away: z.number().int().min(0).max(MAX_GOALS),
  /**
   * Which side wins a shootout, when the prediction is a draw in a match that
   * cannot end level. `'home'` and `'away'` map to the legacy `a` / `b` markers.
   * Only meaningful for single-match knockout ties (for CL 2026/27, the Final).
   */
  penaltyWinner: z.enum(['home', 'away']).nullable().default(null),
});

export type Score = z.infer<typeof scoreSchema>;

/** Health payload, shared so the web app's smoke check is type-safe. */
export const healthSchema = z.object({
  status: z.literal('ok'),
  service: z.string(),
  db: z.enum(['up', 'down']),
});

export type Health = z.infer<typeof healthSchema>;

/**
 * Which sign-in methods are actually usable right now.
 *
 * Google credentials are optional at the environment level (see apps/api/src/env.ts),
 * so the API reports this rather than the client assuming Google is always
 * available — the UI hides the button instead of offering one that fails.
 */
export const authCapabilitiesSchema = z.object({
  google: z.boolean(),
});

export type AuthCapabilities = z.infer<typeof authCapabilitiesSchema>;
